import { Badge } from "@mantine/core";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../../lib/utils";
import type { GroupedAccount, HealthStatusStyles } from "../healthTypes";
import { PAGE_SIZE } from "../healthTypes";
import HealthAccountCard from "./HealthAccountCard";

type HealthKanbanColumnProps = {
  label: string;
  dotColor: string;
  accounts: GroupedAccount[];
  statusStyles: HealthStatusStyles;
  page: number;
  onPageChange: (page: number) => void;
  onCardClick: (account: GroupedAccount) => void;
};

const HealthKanbanColumn = ({
  label,
  dotColor,
  accounts,
  statusStyles,
  page,
  onPageChange,
  onCardClick,
}: HealthKanbanColumnProps) => {
  const totalPages = Math.max(1, Math.ceil(accounts.length / PAGE_SIZE));
  const pagedAccounts = accounts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="flex flex-1 flex-col rounded-lg border border-border bg-muted/30">
      {/* Column Header */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <span className={cn("h-2.5 w-2.5 rounded-full", dotColor)} />
        <span className="text-sm font-medium text-foreground">{label}</span>
        <Badge variant="light" size="sm" className="ml-auto">
          {accounts.length}
        </Badge>
      </div>

      {/* Card List */}
      <div className="flex-1 space-y-2 overflow-y-auto p-3" style={{ maxHeight: "calc(100vh - 320px)" }}>
        {pagedAccounts.length === 0 ? (
          <p className="py-8 text-center text-xs text-muted-foreground">No accounts</p>
        ) : (
          pagedAccounts.map((account) => (
            <HealthAccountCard
              key={account.entityId}
              account={account}
              statusStyles={statusStyles}
              onClick={() => onCardClick(account)}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border px-3 py-2">
          <button
            type="button"
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-[11px] text-muted-foreground">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default HealthKanbanColumn;
